/**
 * Выбор товаров в окне дозагрузки МСК Газобетона: стоимость выбранного, подпись
 * кнопки, разделение по наличию цены и предел количества.
 *
 * Своих цен, норм и вместимости здесь не появляется. Цена читается из строки
 * каталога той же нормализацией, что и раньше (parsePriceValue), паллетоместа
 * считает существующая palletsNeededFor, свободное место приходит готовым из
 * модели машины.
 */

import { parsePriceValue, hasUsablePrice, palletsNeededFor, unitsPerPalletOf } from './gazUpsellMetrics.js';
import { formatMoney } from '../../utils/gazTextFormat.js';

const pluralForm = (count, [one, few, many]) => {
  const n = Math.abs(Math.round(Number(count) || 0));
  const lastTwo = n % 100;
  const lastOne = n % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return many;
  if (lastOne === 1) return one;
  if (lastOne >= 2 && lastOne <= 4) return few;
  return many;
};

/** «1 товар» / «2 товара» / «5 товаров». */
export const itemsWord = (count) => pluralForm(count, ['товар', 'товара', 'товаров']);

/** «1 поддономесто» / «2 поддономеста» / «9 поддономест». */
export const palletPlacesWord = (count) => pluralForm(count, ['поддономесто', 'поддономеста', 'поддономест']);

/** Действующая цена позиции по форме оплаты. */
export const priceOf = (item, paymentMethod) => (
  paymentMethod === 'cash' ? item?.price_cash : item?.price_non_cash
);

const quantityNumber = (value) => {
  const parsed = Number(String(value ?? '').replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

/**
 * Стоимость выбранного: сумма «количество × действующая цена» по каждой позиции.
 * Позиции без пригодной цены в сумму не входят — их и выбрать нельзя.
 *
 * @param {Array}  rows           — выбранные строки [{ item, key }]
 * @param {object} quantities     — введённые количества по ключу строки
 * @param {string} paymentMethod  — форма оплаты
 */
export const selectionTotalPrice = ({ rows, quantities, paymentMethod } = {}) => (
  (Array.isArray(rows) ? rows : []).reduce((sum, row) => {
    const price = parsePriceValue(priceOf(row?.item, paymentMethod));
    if (price === null || price <= 0) {
      return sum;
    }
    return sum + quantityNumber(quantities?.[row?.key]) * price;
  }, 0)
);

/** «Добавить 3 товара · 126 500 ₽»; без выбора — «Выберите товары». */
export const addButtonLabel = ({ count, total } = {}) => {
  const items = Math.max(Math.round(Number(count) || 0), 0);
  if (items === 0) {
    return 'Выберите товары';
  }
  return `Добавить ${items} ${itemsWord(items)} · ${formatMoney(total)} ₽`;
};

/**
 * Разделение списка на доступные к выбору и позиции без цены.
 * Товары без цены не занимают начало списка — они уходят отдельной группой вниз.
 */
export const splitByPrice = (rows, paymentMethod) => {
  const list = Array.isArray(rows) ? rows : [];
  const priced = [];
  const priceless = [];
  for (const row of list) {
    if (hasUsablePrice(priceOf(row?.item, paymentMethod))) {
      priced.push(row);
    } else {
      priceless.push(row);
    }
  }
  return { priced, priceless };
};

/**
 * Состояние свободного места окна дозагрузки — одно на всё окно.
 *
 * Своей вместимости не считает: свободные паллетоместа приходят готовыми из модели
 * машины, занятые выбором — уже сложенной суммой по строкам.
 *
 * `isFull` — место разобрано выбором целиком. Это не ошибка: причина в собственном
 * выборе менеджера, поэтому окно показывает одно спокойное сообщение, а не красную
 * строку у каждой оставшейся позиции. Превышение (`isOver`) — отдельное состояние
 * со своим предупреждением.
 *
 * @param {number|null} freePallets    — свободные паллетоместа машины
 * @param {number}      selectedPallets — занято текущим выбором
 * @param {number}      selectedCount   — сколько позиций выбрано
 * @returns {{remaining:number|null, isOver:boolean, isFull:boolean}}
 */
export const upsellCapacityState = ({ freePallets, selectedPallets, selectedCount } = {}) => {
  const free = Number(freePallets);
  if (freePallets === null || freePallets === undefined || !Number.isFinite(free)) {
    return { remaining: null, isOver: false, isFull: false };
  }

  const selected = Number(selectedPallets) || 0;
  const count = Math.max(Math.round(Number(selectedCount) || 0), 0);
  const remaining = free - selected;
  const isOver = selected > free;

  return {
    remaining,
    isOver,
    isFull: count > 0 && remaining <= 0 && !isOver,
  };
};

/**
 * Сколько ещё поместится этой позиции.
 *
 * Учитывается собственное текущее количество: место, которое позиция уже заняла в
 * выборе, для неё же остаётся доступным. Иначе после выбора нескольких позиций
 * подсказка занижала бы предел.
 *
 * @param {object} item              — строка каталога
 * @param {number} allowancePallets  — свободные паллетоместа за вычетом других позиций
 * @param {*}      currentQuantity   — текущее количество этой позиции
 * @returns {{maxPallets:number,maxUnits:number|null,perPallet:number|null}|null}
 *   null — нормы поддона нет, предел определить нечем.
 */
export const maxQuantityFor = ({ item, allowancePallets, currentQuantity } = {}) => {
  const perPallet = unitsPerPalletOf(item);
  const ownPallets = palletsNeededFor(item, currentQuantity);

  if (perPallet === null && ownPallets === null) {
    return null;
  }

  const allowance = Number(allowancePallets);
  if (!Number.isFinite(allowance)) {
    return null;
  }

  const maxPallets = Math.max(Math.floor(allowance), 0);

  return {
    maxPallets,
    perPallet,
    maxUnits: perPallet ? maxPallets * perPallet : null,
  };
};

export default selectionTotalPrice;
